import { EMPTY_ARRAY, EMPTY_STRING, KeyNames } from "@regrello/core-utils";
import type { FieldInstanceFields } from "@regrello/graphql-api";
import { type RegrelloDataGridV2EditableCellProps, useDataGridV2EditableCell } from "@regrello/ui-core";
import { useCallback, useRef } from "react";
import { isWithinElement } from "../../../../../../../utils/isWithinElement";
import { isItemsEqual, itemPredicate } from "../../../../customFields/plugins/MultiSelectFieldPlugin";
import { extractAllowedValuesFromFieldVersionForFrontend } from "../../../../customFields/plugins/utils/extractAllowedValuesForFrontend";
import { RegrelloFormFieldMultiSelect } from "../../../../formFields/RegrelloFormFieldMultiSelect";
import type { MultiSelectPluginFrontendValue } from "./types";

export interface RegrelloEditableCellMultiSelectProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: MultiSelectPluginFrontendValue | null) => void;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: MultiSelectPluginFrontendValue | null;
}

/**
 * An editable cell in which the user can select multiple items from a list. Intended for use in
 * `RegrelloDataGridV2`.
 */
export function RegrelloEditableCellMultiSelect({
  api,
  eventKey,
  fieldInstance,
  value,
  onValueChange,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellMultiSelectProps>) {
  const editableCellRef = useRef<HTMLDivElement>(null);
  const initialInputValue = eventKey != null && eventKey !== KeyNames.ENTER ? eventKey : EMPTY_STRING;

  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersion is unexpectedly not defined");
  }

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({
    api,
    eventKey,
    // These keys are used to cycle through menu items:
    suppressKeyArrowDown: true,
    suppressKeyArrowUp: true,
  });

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      // (chase): If the options menu closes, stop editing. This mirrors the
      // behavior of single select cells.
      if (!isOpen) {
        api.stopEditing();
      }
    },
    [api],
  );

  return (
    <div ref={editableCellRef} style={{ width: columnWidth }}>
      <RegrelloFormFieldMultiSelect
        autoFocus={true}
        getSelectedItemProps={(item) => {
          return {
            children: item.displayValue,
          };
        }}
        inputProps={{
          // For this component we want to stop editing `after` value was selected
          onKeyDown: onKeyDownCapture,
          value: initialInputValue,
        }}
        interactionMode="editableTableCell"
        isDefaultMarginsOmitted={true} // (jullman): Necessary to prevent cell from jumping up toward bottom of the table.
        isItemsEqual={isItemsEqual}
        itemPredicate={itemPredicate}
        items={extractAllowedValuesFromFieldVersionForFrontend(fieldInstance.spectrumFieldVersion)}
        onBlur={(event) => {
          const isWithinInput = isWithinElement(
            event.relatedTarget as HTMLElement | null | undefined,
            editableCellRef.current,
          );

          if (!isWithinInput) {
            api.stopEditing();
          }
        }}
        onChange={onValueChange}
        onOpenChange={onOpenChange}
        renderItem={(item) => {
          return {
            key: item.id,
            text: item.displayValue,
          };
        }}
        size="x-small"
        value={value ?? EMPTY_ARRAY}
      />
    </div>
  );
}
